import { z } from "zod";

/**
 * A rate as the European Central Bank states it: how much of a currency one euro buys, a positive decimal string kept
 * exactly as published ("0.86045"), never a float.
 */
const referenceRateSchema = z
	.string()
	.regex(/^\d{1,7}(?:\.\d{1,6})?$/)
	.refine(rate => Number(rate) > 0, "A rate is more than nothing");

/**
 * The euro foreign exchange reference rates the ECB publishes each working day at about 16:00 CET, as the API last read
 * them: the day they are for, and one rate per currency against the euro, which is their base and never among them.
 */
export const referenceRatesSchema = z.strictObject({
	status: z.literal("available"),
	source: z.literal("ECB"),
	base: z.literal("EUR"),
	date: z.iso.date(),
	rates: z
		.record(z.string().regex(/^[A-Z]{3}$/), referenceRateSchema)
		.refine(rates => !Object.hasOwn(rates, "EUR"), "The euro is the base, not a rate")
		.refine(rates => Object.keys(rates).length > 0, "A day of rates has at least one"),
});
export type ReferenceRates = z.infer<typeof referenceRatesSchema>;

/**
 * What `GET /api/exchange-rates` answers: the ECB's reference rates, or that the API could not read them — which never
 * fails anything else, and never stands in for a rate.
 */
export const exchangeRatesSchema = z.discriminatedUnion("status", [
	z.strictObject({ status: z.literal("unavailable") }),
	referenceRatesSchema,
]);
export type ExchangeRates = z.infer<typeof exchangeRatesSchema>;

/**
 * The ECB's reference rates published for Friday 2026-09-25, all 29 currencies it quotes. The local API and the
 * frontend's fake API serve them for the demo organization (`demoReferenceRates`); nothing in AWS does.
 */
const DEMO_RATES: Readonly<Record<string, string>> = {
	USD: "1.1403",
	JPY: "179.70",
	CZK: "24.345",
	DKK: "7.4755",
	GBP: "0.86045",
	HUF: "365.28",
	PLN: "4.3718",
	RON: "5.2765",
	SEK: "11.2900",
	CHF: "0.9445",
	ISK: "136.60",
	NOK: "10.8400",
	TRY: "55.7975",
	AUD: "1.6220",
	BRL: "5.9091",
	CAD: "1.6127",
	CNY: "7.6551",
	HKD: "8.9445",
	IDR: "20427.22",
	ILS: "3.4594",
	INR: "109.2605",
	KRW: "1545.16",
	MXN: "20.1816",
	MYR: "4.6456",
	NZD: "2.0108",
	PHP: "71.244",
	SGD: "1.4563",
	THB: "38.023",
	ZAR: "18.5798",
};

/** The last Monday to Friday on or before `now`, in UTC: the latest day the ECB could have published rates for. */
function latestWeekday(now: number): string {
	const today = new Date(now);
	const weekday = today.getUTCDay();
	let back = 0;
	if (weekday === 0) {
		back = 2;
	} else if (weekday === 6) {
		back = 1;
	}
	return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - back))
		.toISOString()
		.slice(0, 10);
}

/**
 * The demo organization's reference rates: the ECB's real rates of 2026-09-25, dated the latest weekday on or before
 * `now`, so the local API and the fake API never look out of date. Shared by both, so they always agree.
 */
export function demoReferenceRates(now: number = Date.now()): ReferenceRates {
	return { status: "available", source: "ECB", base: "EUR", date: latestWeekday(now), rates: { ...DEMO_RATES } };
}
